import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import SmartLink from '@/components/SmartLink';

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Sparticle Inc.',
  url: 'https://cs.gbase.ai/',
  logo: 'https://cs.gbase.ai/title-logo.png',
  sameAs: [
    'https://x.com/sparticle_ai',
    'https://note.com/sparticle',
    'https://www.linkedin.com/company/sparticle-inc',
  ],
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://cs.gbase.ai/' },
    { '@type': 'ListItem', position: 2, name: '料金プラン', item: 'https://cs.gbase.ai/pricing' },
  ],
};

const PRODUCT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'GBase Support',
  description:
    '自社データを学習し、ウェブサイト・社内ナレッジ・LINE・店舗まで横断して問い合わせ対応を自動化する AI カスタマーサポート（AIチャットボット）プラットフォーム。',
  brand: { '@type': 'Brand', name: 'Sparticle' },
  url: 'https://cs.gbase.ai/',
  image: 'https://cs.gbase.ai/title-logo.png',
  category: 'AI Customer Support / AI Chatbot',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'JPY',
    availability: 'https://schema.org/InStock',
    url: 'https://cs.gbase.ai/pricing',
  },
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '初期費用の目安はどれくらいですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '規模・接続データソース数・カスタマイズ範囲により変動します。アカウント開設・初期セットアップ・標準データ取込・基本トレーニングを含みます。業界特有のシステム連携や大量データ移行が発生する場合は別途追加費用が発生します。ヒアリング後に詳細な内訳を含むお見積もりを提示いたします。',
      },
    },
    {
      '@type': 'Question',
      name: '14日間無料トライアルは利用できますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、ご提供しています。営業担当によるヒアリング後、貴社の業務環境に合わせたトライアル環境（14日間）をご用意します。トライアル期間中は専任担当が活用サポートを行い、本格導入時の最適な構成をご提案します。お問い合わせフォームよりご連絡ください。',
      },
    },
    {
      '@type': 'Question',
      name: '月間メッセージ数を超えるとどうなりますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '月間メッセージ数の上限を超えた場合、月内は応答が一時的に制限されることがあります。継続的にご利用が増えている場合は、上位プランへの切替をお勧めします。プラン切替は営業担当にご連絡いただければ柔軟に対応いたします。',
      },
    },
    {
      '@type': 'Question',
      name: 'どのプランから始めるのが適切ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'スタッフ規模・想定される月間問い合わせ件数・接続したいデータソースをお伺いし、最適な構成をご提案します。利用量が増えた際は、営業担当にご連絡いただくことで上位構成へのスムーズな切替が可能です。',
      },
    },
    {
      '@type': 'Question',
      name: '請求書払い・法人カードは利用できますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'どちらも対応しています。クレジットカード払い、銀行振込（請求書払い）から選択可能で、年次一括払いをご選択いただく場合は別途割引のご相談も承ります。',
      },
    },
    {
      '@type': 'Question',
      name: '契約期間・解約条件は？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '契約は年次を基本とし、ご利用条件に応じて柔軟にご相談可能です。解約・契約変更については営業担当が個別にサポートいたします。詳細はお問い合わせください。',
      },
    },
    {
      '@type': 'Question',
      name: '導入後にプランをアップグレード／ダウングレードできますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、いつでも変更可能です。利用規模・必要機能の変化に応じた段階的な切替に対応しており、営業担当にご連絡いただければスムーズに新構成へ移行できます。',
      },
    },
    {
      '@type': 'Question',
      name: 'セキュリティ・データ保管場所は？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '日本国内の認証取得済データセンターで運用しており、保管時・通信時ともにデータを暗号化しています。エンタープライズプランでは専用テナント・オンプレミス導入オプションも提供します。詳細なセキュリティ仕様書・コンプライアンス文書は別途お渡し可能です。',
      },
    },
  ],
};

type FeatureItem = {
  title: string;
  desc: React.ReactNode;
  svg: React.ReactNode;
};

const InlineLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    style={{
      color: '#7C3AED',
      fontWeight: 600,
      textDecoration: 'underline',
      textDecorationColor: 'rgba(139, 92, 246, 0.28)',
      textUnderlineOffset: '3px',
    }}
  >
    {children}
  </a>
);

const FEATURES: FeatureItem[] = [
  {
    title: 'RAG チャットボット',
    desc: (
      <>
        自社ドキュメント・FAQ・ウェブサイトから高精度な応答を生成。詳細は
        <InlineLink href="https://cs.gbase.ai/blog/2026-04-27-chatbot-mechanism">チャットボットの仕組み</InlineLink>
        もご参照ください。
      </>
    ),
    svg: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    title: 'AI コンシェルジュ',
    desc: '自然対話での案内・推薦・在庫照会など、接客 AI として活用。',
    svg: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
  {
    title: '音声対話',
    desc: 'WebSocket リアルタイム双方向音声で、AI 機能を音声で操作可能。',
    svg: (
      <>
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </>
    ),
  },
  {
    title: '多言語対応',
    desc: '日・英・中・韓を含む 10+ 言語に対応。インバウンド・海外拠点に。',
    svg: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    ),
  },
  {
    title: 'マルチチャネル連携',
    desc: 'LINE / Microsoft Teams / WhatsApp / Web ウィジェット 一括展開。',
    svg: (
      <>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </>
    ),
  },
  {
    title: 'MCP 連携',
    desc: 'Model Context Protocol で外部システム・API・カスタム業務に統合。',
    svg: (
      <>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </>
    ),
  },
  {
    title: 'Digital Human',
    desc: '2D アバター + 音声合成によるバーチャル接客。コスト 80-95% 削減。',
    svg: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      </>
    ),
  },
  {
    title: '解決率分析',
    desc: '解決率・ナレッジギャップ・利用傾向を可視化、PDCA サイクルを継続的に運用。',
    svg: (
      <>
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </>
    ),
  },
];

type AddonItem = {
  title: string;
  desc: string;
  svg: React.ReactNode;
  link?: { href: string; label: string };
};

const ADDONS: AddonItem[] = [
  {
    title: '精度測定＆改善サービス',
    desc: '応答精度を定期的に測定し、業界・業務特有の専門用語や応答スタイルに合わせた応答モデルの継続的なチューニング。',
    svg: (
      <>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </>
    ),
  },
  {
    title: 'オンプレミス導入',
    desc: '機密性が高い業務向けに、ソフトウェア＋ハードウェア一体型の専用「GBase OnPrem」製品でご提供。完全閉域・データ主権を確保した運用を実現。',
    link: { href: 'https://onprem.gbase.ai/', label: '詳細を見る ↗' },
    svg: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    ),
  },
  {
    title: 'カスタム連携開発',
    desc: '標準カタログにない社内システム・基幹業務システムへの専用コネクタ開発。',
    svg: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    ),
  },
  {
    title: '多言語対応強化',
    desc: '10+ 言語対応に加え、特定方言・専門用語辞書の追加。インバウンド対応に。',
    svg: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    title: '専任 CSM',
    desc: '業務に伴走する専任カスタマーサクセスが定例レビュー・活用度分析・改善提案を継続実施。',
    svg: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    title: 'セキュリティ監査対応',
    desc: '第三者監査・脆弱性診断・コンプライアンス文書（ISO / SOC2 等）の提供。',
    svg: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    title: 'Digital Human（Live2D）',
    desc: 'Live2D 動的アバターで表情・身振り付きの高度な接客体験を実現。コスト 80-95% 削減。',
    svg: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </>
    ),
  },
  {
    title: '大規模データ移行',
    desc: '既存ナレッジベース・FAQ集・社内 Wiki からの一括取込・クレンジング支援。',
    svg: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
  },
];

const AddonCard: React.FC<{ item: AddonItem }> = ({ item }) => (
  <div
    className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(139,92,246,0.08)] hover:border-[rgba(139,92,246,0.28)]"
    style={{
      background: '#FFFFFF',
      border: '1px solid #E5E4EC',
      borderRadius: 14,
      padding: '1.5rem 1.75rem',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.85rem',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#8B5CF6',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width={18}
          height={18}
          stroke="currentColor"
          strokeWidth={2}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {item.svg}
        </svg>
      </div>
      {item.link && (
        <a
          href={item.link.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: '#8B5CF6',
            background: 'rgba(139, 92, 246, 0.1)',
            padding: '0.32rem 0.75rem',
            borderRadius: 999,
            border: '1px solid rgba(139, 92, 246, 0.28)',
            whiteSpace: 'nowrap',
          }}
        >
          {item.link.label}
        </a>
      )}
    </div>
    <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#1A1B26', marginBottom: '0.4rem' }}>{item.title}</h4>
    <p style={{ fontSize: '0.85rem', color: '#555573', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
  </div>
);

const SECURITY: FeatureItem[] = [
  {
    title: 'ロール別アクセス制御',
    desc: 'RBAC で部署・役職に応じた権限を細かく管理。',
    svg: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </>
    ),
  },
  {
    title: '国内データセンター',
    desc: '日本国内の認証取得済 DC で運用、監査対応も万全。',
    svg: (
      <>
        <circle cx="12" cy="8" r="6" />
        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
      </>
    ),
  },
  {
    title: 'データ暗号化',
    desc: '保管時・通信時ともに暗号化された安全な基盤。',
    svg: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
  {
    title: '仕様書・規約',
    desc: 'セキュリティ仕様書・規約を提供。稟議もスムーズ。',
    svg: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
  },
];

const SUPPORT: FeatureItem[] = [
  {
    title: '標準サポート',
    desc: '業務時間内のメール・チャット対応。エンタープライズは 24/7。',
    svg: (
      <>
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3zM3 19a2 2 0 0 0 2 2h1v-7H3z" />
      </>
    ),
  },
  {
    title: '常時アップデート',
    desc: '新機能・改善は追加費用なしで自動配信。',
    svg: (
      <>
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </>
    ),
  },
  {
    title: 'ヘルプセンター',
    desc: '使い方ガイド・API ドキュメント・動画を提供。',
    svg: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </>
    ),
  },
  {
    title: '解決率レポート',
    desc: '問い合わせ解決率・ナレッジギャップを可視化、改善サイクルを継続。',
    svg: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <line x1="6" y1="8" x2="10" y2="8" />
        <line x1="6" y1="12" x2="14" y2="12" />
      </>
    ),
  },
];

const SHOWCASE_TAG_STYLE: React.CSSProperties = {
  display: 'inline-block',
  fontSize: '0.74rem',
  fontWeight: 700,
  padding: '0.35rem 0.95rem',
  borderRadius: 999,
  marginBottom: '1.25rem',
  letterSpacing: '0.06em',
};

const NUM_BADGE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 6,
  color: '#FFFFFF',
  fontSize: '0.72rem',
  fontWeight: 800,
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: '#555573',
  marginBottom: '0.5rem',
  letterSpacing: '0.05em',
};

const TAG_STYLE: React.CSSProperties = {
  display: 'inline-block',
  fontSize: '0.72rem',
  fontWeight: 600,
  padding: '0.22rem 0.7rem',
  borderRadius: 999,
};

const DESC_STYLE: React.CSSProperties = {
  fontSize: '0.85rem',
  color: '#555573',
  lineHeight: 1.6,
  marginTop: 'auto',
};

const PLUS_STYLE: React.CSSProperties = {
  color: '#94939F',
  fontSize: '1.4rem',
  fontWeight: 300,
};

type IconVariant = 'brand' | 'blue' | 'green';

const ICON_BG: Record<IconVariant, string> = {
  brand: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)',
  blue: '#EFF6FF',
  green: '#F0FDF4',
};

const ICON_COLOR: Record<IconVariant, string> = {
  brand: '#FFFFFF',
  blue: '#2563EB',
  green: '#16A34A',
};

const ShowcaseCard: React.FC<{
  item: { title: string; desc: React.ReactNode; svg: React.ReactNode };
  variant?: IconVariant;
}> = ({ item, variant = 'brand' }) => (
  <div
    className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(139,92,246,0.08)] hover:border-[rgba(139,92,246,0.28)]"
    style={{
      background: '#FFFFFF',
      border: '1px solid #E5E4EC',
      borderRadius: 14,
      padding: '1.5rem 1.25rem',
      textAlign: 'center',
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        margin: '0 auto 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: ICON_BG[variant],
        borderRadius: 11,
        color: ICON_COLOR[variant],
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width={20}
        height={20}
        stroke="currentColor"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {item.svg}
      </svg>
    </div>
    <h5 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1A1B26', marginBottom: '0.3rem' }}>{item.title}</h5>
    <p style={{ fontSize: '0.8rem', color: '#555573', lineHeight: 1.55, margin: 0 }}>{item.desc}</p>
  </div>
);

const ShowcaseGrid: React.FC<{
  items: { title: string; desc: React.ReactNode; svg: React.ReactNode }[];
  variant?: IconVariant;
}> = ({ items, variant }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
    {items.map((item) => (
      <ShowcaseCard key={item.title} item={item} variant={variant} />
    ))}
  </div>
);

const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: `1px solid ${open ? '#8B5CF6' : '#E5E4EC'}`,
        borderRadius: 12,
        padding: '1.25rem 1.5rem',
        marginBottom: '0.7rem',
        boxShadow: open ? '0 8px 24px rgba(139, 92, 246, 0.08)' : 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          fontWeight: 700,
          color: '#1A1B26',
          fontSize: '0.95rem',
          position: 'relative',
          paddingRight: '2rem',
          fontFamily: 'inherit',
        }}
      >
        {question}
        <span
          style={{
            position: 'absolute',
            right: 0,
            top: '-0.1rem',
            fontSize: '1.4rem',
            fontWeight: 300,
            color: '#8B5CF6',
            transform: open ? 'rotate(45deg)' : 'rotate(0)',
            transition: 'transform 0.25s',
            display: 'inline-block',
            lineHeight: 1,
          }}
        >
          +
        </span>
      </button>
      {open && (
        <p style={{ marginTop: '0.7rem', color: '#555573', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: 0 }}>
          {answer}
        </p>
      )}
    </div>
  );
};

const FaqList: React.FC<{ items: { name: string; acceptedAnswer: { text: string } }[] }> = ({ items }) => (
  <div style={{ maxWidth: 820, margin: '0 auto' }}>
    {items.map((q) => (
      <FaqItem key={q.name} question={q.name} answer={q.acceptedAnswer.text} />
    ))}
  </div>
);

const PricingComposition: React.FC = () => (
  <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
    {/* Row: Card 1 + Card 2 */}
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
      {/* Card 1: 初期費用 (yellow) */}
      <div
        style={{
          flex: '1 1 0',
          minWidth: 280,
          border: '1.5px solid #EAB308',
          borderRadius: 16,
          padding: '1.75rem 1.5rem 1.5rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%)',
          boxShadow: '0 12px 32px rgba(234, 179, 8, 0.12)',
        }}
      >
        <span style={{ ...NUM_BADGE, background: '#EAB308', margin: '0 auto 0.85rem' }}>1</span>
        <span style={LABEL_STYLE}>初期費用</span>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ ...TAG_STYLE, background: 'rgba(234, 179, 8, 0.14)', color: '#854D0E' }}>規模に応じて</span>
        </div>
        <p style={DESC_STYLE}>アカウント開設・初期セットアップ・標準データ取込・業務テンプレート構築を含みます。</p>
      </div>

      {/* Plus separator (desktop only) */}
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.25rem', ...PLUS_STYLE }}
      >
        ＋
      </div>

      {/* Card 2: 月額費用 (brand violet) */}
      <div
        style={{
          flex: '1 1 0',
          minWidth: 280,
          border: '1.5px solid #7C68FB',
          borderRadius: 16,
          padding: '1.75rem 1.5rem 1.5rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, rgba(124, 104, 251, 0.06) 0%, #FFFFFF 75%)',
          boxShadow: '0 12px 32px rgba(124, 104, 251, 0.14)',
        }}
      >
        <span style={{ ...NUM_BADGE, background: '#7C68FB', margin: '0 auto 0.85rem' }}>2</span>
        <span style={LABEL_STYLE}>月額費用</span>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ ...TAG_STYLE, background: '#F4F2FA', color: '#555573' }}>プランに応じて</span>
        </div>
        <p style={DESC_STYLE}>月間メッセージ数・接続データソース数・利用機能範囲に応じた月額ライセンス費。</p>
      </div>
    </div>

    {/* Plus between row and card 3 */}
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.25rem 0', ...PLUS_STYLE }}
    >
      ＋
    </div>

    {/* Card 3: オプション (accent blue, wide) */}
    <div
      style={{
        border: '1.5px solid #2563EB',
        borderRadius: 16,
        padding: '1.25rem 1.75rem',
        textAlign: 'left',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '1rem 1.5rem',
        alignItems: 'center',
        background: 'linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)',
        boxShadow: '0 12px 32px rgba(37, 99, 235, 0.12)',
      }}
    >
      <span style={{ ...NUM_BADGE, background: '#2563EB' }}>3</span>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.4rem' }}>
          <span style={{ ...LABEL_STYLE, display: 'inline', marginBottom: 0, marginRight: '0.4rem' }}>オプション</span>
          <span style={{ ...TAG_STYLE, background: 'rgba(37, 99, 235, 0.12)', color: '#1D4ED8' }}>追加要件のみ</span>
        </div>
      </div>
      <p style={{ ...DESC_STYLE, marginTop: 0, gridColumn: 2 }}>
        標準カタログ範囲を超える要件（カスタム連携・精度測定＆改善・オンプレ・専任 CSM
        等）のみ追加で選択可能。導入後の段階的追加にも柔軟に対応します。
      </p>
    </div>
  </div>
);

const TIMELINE = [
  { num: 1, title: 'お問い合わせ', desc: 'フォーム・電話・メールより、業務要件・規模感をお伝えください。' },
  { num: 2, title: 'ヒアリング・見積', desc: '規模感・接続範囲を確認し、最適プランと詳細見積をご提示します。' },
  {
    num: 3,
    title: 'ナレッジ取込',
    desc: 'FAQ・社内文書・ウェブサイトを学習データとして取込み、応答モデルを構築します。',
  },
  { num: 4, title: 'テスト・調整', desc: '少人数で実業務テスト、応答品質を確認・チューニングします。' },
  { num: 5, title: '本番運用', desc: 'ウィジェット / LINE / Teams 等への展開後、継続的に活用を支援します。' },
];

const Pricing: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SimpleLayout>
      <Helmet>
        <title>チャットボット 料金・価格｜月額・初期費用・導入費用 | GBase Support</title>
        <meta
          name="description"
          content="チャットボット 料金・価格・導入費用を解説。自社データ学習で応答精度を高め、問い合わせ削減を実現するエンタープライズ AI チャットボット。規模・要件に応じた最適構成を、ヒアリング後にお見積もりにてご提示。14日間無料トライアルあり。"
        />
        <meta
          name="keywords"
          content="チャットボット 料金, チャットボット 価格, チャットボット 導入費用, チャットボット 比較, チャットボット おすすめ, チャットボット 選び方, カスタマーサポート チャットボット, GBase Support 価格"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://cs.gbase.ai/pricing" />
        <meta property="og:title" content="チャットボット 料金・価格｜月額・初期費用・導入費用｜GBase Support" />
        <meta
          property="og:description"
          content="チャットボット 料金・価格・導入費用を解説。自社データ学習・問い合わせ削減を実現するエンタープライズ AI チャットボット。規模・要件に応じた最適構成をヒアリング後にお見積もりでご提示。"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cs.gbase.ai/pricing" />
        <meta property="og:image" content="https://cs.gbase.ai/pricing-og.png" />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:site_name" content="GBase Support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="チャットボット 料金・価格｜月額・初期費用・導入費用｜GBase Support" />
        <meta
          name="twitter:description"
          content="チャットボット 料金・価格・導入費用を解説。自社データ学習・問い合わせ削減を実現する AI チャットボット。規模に応じた最適構成をヒアリング後にお見積もり。"
        />
        <meta name="twitter:image" content="https://cs.gbase.ai/pricing-og.png" />
        <script type="application/ld+json">{JSON.stringify(PRODUCT_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <div className="bg-white text-gray-900">
        {/* ===== PAGE TITLE ===== */}
        <section
          className="pt-16 pb-12 px-8 text-center border-b border-gray-200"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(139, 92, 246, 0.16) 0%, transparent 75%), linear-gradient(180deg, rgba(139, 92, 246, 0.06) 0%, rgba(139, 92, 246, 0.02) 100%)',
          }}
        >
          <h1
            className="text-3xl md:text-[2rem] font-extrabold text-gray-900 mb-2.5"
            style={{ letterSpacing: '-0.02em' }}
          >
            GBase Support 料金・価格
          </h1>
          <p className="text-sm md:text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            ビジネスの規模・利用範囲・連携要件に応じて、最適な構成と詳細なお見積もりをご提案します。
          </p>
        </section>

        {/* ===== PRICING COMPOSITION ===== */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">料金は3つの要素で構成</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                初期費用なし、月額ライセンスとオプションのみ。CAPEX 不要・社内予算稟議もスムーズに。
              </p>
            </div>

            <PricingComposition />
          </div>
        </section>

        {/* ===== FEATURE INTRODUCTION ===== */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">GBase Support でできること</h2>
              <p className="text-gray-600 max-w-3xl mx-auto" style={{ fontSize: '0.95rem' }}>
                自社データを学習し、ウェブ・社内・LINE・店舗まで横断して問い合わせ対応を自動化。すべてのプランで以下の中核機能をご利用いただけます。
                <br className="md:hidden" />
                ナレッジ統合の考え方は
                <InlineLink href="https://cs.gbase.ai/blog/knowledge-management-tools">
                  ナレッジ管理ツール比較
                </InlineLink>
                もあわせてご参照ください。
              </p>
            </div>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <ShowcaseGrid items={FEATURES} />
            </div>
          </div>
        </section>

        {/* ===== ADD-ONS ===== */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">追加オプション</h2>
              <p className="text-gray-600 max-w-2xl mx-auto" style={{ fontSize: '0.95rem' }}>
                標準プラン外の要件はオプションで柔軟に追加。導入後の段階的な拡張にも対応します。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ maxWidth: 1100, margin: '0 auto' }}>
              {ADDONS.map((a) => (
                <AddonCard key={a.title} item={a} />
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECURITY & SUPPORT ===== */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">セキュリティ & サポート体制</h2>
              <p className="text-gray-600 max-w-2xl mx-auto" style={{ fontSize: '0.95rem' }}>
                国内 DC 運用・暗号化基盤と、お客様の活用を継続的に支える運用サポート体制。
              </p>
            </div>

            {/* セキュリティ band */}
            <div style={{ maxWidth: 1100, margin: '0 auto 2rem' }}>
              <span style={{ ...SHOWCASE_TAG_STYLE, color: '#0369A1', background: 'rgba(56, 189, 248, 0.14)' }}>
                セキュリティ
              </span>
              <ShowcaseGrid items={SECURITY} variant="blue" />
            </div>

            {/* サポート・運用 band */}
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <span style={{ ...SHOWCASE_TAG_STYLE, color: '#15803D', background: 'rgba(34, 197, 94, 0.14)' }}>
                サポート・運用
              </span>
              <ShowcaseGrid items={SUPPORT} variant="green" />
            </div>
          </div>
        </section>

        {/* ===== IMPLEMENTATION FLOW ===== */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">最短 1 週間で本番稼働</h2>
              <p className="text-gray-600 max-w-3xl mx-auto" style={{ fontSize: '0.95rem' }}>
                お問い合わせから本番運用まで、Sparticle のカスタマーサクセスチームが伴走支援します。
                <br className="md:hidden" />
                導入後の効果指標は
                <InlineLink href="https://cs.gbase.ai/blog/cs-kpi">カスタマーサポート KPI ガイド</InlineLink>
                もご活用ください。
              </p>
            </div>

            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5"
              style={{ maxWidth: 1100, margin: '0 auto', gap: '0.5rem' }}
            >
              {TIMELINE.map((t, idx) => (
                <div key={t.num} style={{ textAlign: 'center', position: 'relative', padding: '0 0.5rem' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      margin: '0 auto 0.85rem',
                      background: 'rgba(139, 92, 246, 0.1)',
                      color: '#8B5CF6',
                      border: '1px solid rgba(139, 92, 246, 0.28)',
                      padding: '0.32rem 0.95rem',
                      borderRadius: 999,
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: '0.06em',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <span style={{ opacity: 0.7, fontWeight: 600 }}>STEP </span>
                    {t.num}
                  </span>
                  {idx < TIMELINE.length - 1 && (
                    <span
                      className="hidden md:block"
                      style={{
                        position: 'absolute',
                        top: 14,
                        right: '-10%',
                        width: '20%',
                        height: 1,
                        background: '#D6D5E0',
                      }}
                    />
                  )}
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1A1B26', marginBottom: '0.4rem' }}>
                    {t.title}
                  </h4>
                  <p style={{ fontSize: '0.8rem', color: '#555573', lineHeight: 1.55, margin: 0 }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">料金・契約についてよくあるご質問</h2>
              <p className="text-gray-600" style={{ fontSize: '0.95rem' }}>
                よくいただくご質問にお答えします。さらに詳しい内容はお気軽にお問い合わせください。
              </p>
            </div>
            <FaqList items={FAQ_SCHEMA.mainEntity} />
          </div>
        </section>

        {/* ===== CTA ===== */}
        <section
          style={{
            padding: '5rem 2rem',
            textAlign: 'center',
            background:
              'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(139, 92, 246, 0.22) 0%, transparent 75%), linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)',
            borderTop: '1px solid #E5E4EC',
          }}
        >
          <h2 style={{ fontSize: '1.85rem', fontWeight: 800, marginBottom: '0.85rem', color: '#1A1A2E' }}>
            貴社に最適なプランをご提案します
          </h2>
          <p
            style={{
              color: '#555573',
              marginBottom: '1.75rem',
              maxWidth: 880,
              marginLeft: 'auto',
              marginRight: 'auto',
              fontSize: '0.95rem',
            }}
          >
            ビジネス規模・利用範囲・連携要件をお伺いし、最適な構成と詳細なお見積もりをご返答します。
            <br />
            お気軽にお問い合わせください。
            <br />
            <a
              href="https://cs.gbase.ai/blog/chatbot-effect-guide-2026"
              style={{
                color: 'inherit',
                opacity: 0.85,
                fontWeight: 600,
                textDecoration: 'underline',
                textDecorationColor: 'rgba(139, 92, 246, 0.28)',
                textUnderlineOffset: '3px',
              }}
            >
              顧客対応時間短縮ガイド
            </a>
            もあわせてご活用ください。
          </p>
          <SmartLink
            to="/contact"
            className="inline-flex items-center justify-center"
            // eslint-disable-next-line react/forbid-component-props
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.7rem 1.6rem',
                borderRadius: 999,
                fontWeight: 600,
                fontSize: '0.92rem',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                color: '#FFFFFF',
                border: '1.5px solid transparent',
                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              お問い合わせ
            </span>
          </SmartLink>
        </section>
      </div>
    </SimpleLayout>
  );
};

export default Pricing;
